import { zodResolver } from "@hookform/resolvers/zod";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faEye, faLock } from "@fortawesome/pro-regular-svg-icons";

import { checkPasswordComplexity } from "check-password-complexity";

import classNames from "classnames";

import { Icon } from "@/components";
import { TokenPurpose } from "@/enum";

import { ROUTE_CONFIG } from "@/routes/config";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import FormContainer from "../../components/FormContainer";
import { useSetPasswordMutation } from "../../redux/auth.api";
import { SetPasswordSchema, SetPasswordType } from "./schema/set-password";

export const ChangePassword: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [passwordFieldType, setPasswordFieldType] = useState("password");
	const [passwordComplexity, setPasswordComplexity] = useState<number>(0);

	const [setPassword, { isLoading, error }] = useSetPasswordMutation();
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<SetPasswordType>({
		defaultValues: {
			password: "",
			confirmPassword: "",
			termsAndConditions: true,
		},
		resolver: zodResolver(SetPasswordSchema),
	});

	const onSubmit = async (values: SetPasswordType) => {
		try {
			await setPassword({
				token: searchParams.get("token") || "",
				purpose: TokenPurpose.FORGOTTEN_PASSWORD,
				password: values.password,
				confirmPassword: values.confirmPassword,
			}).unwrap();
			navigate(ROUTE_CONFIG.LOGIN);
		} catch (err) {
			console.error(err);
		}
	};

	const formValues = watch();

	useEffect(() => {
		if (formValues.password) {
			const complexity = checkPasswordComplexity(formValues.password);

			setPasswordComplexity(complexity.score);
		} else {
			setPasswordComplexity(0);
		}
	}, [formValues.password]);

	const passwordIncludesNumbers = useMemo(() => {
		return formValues.password.match(/\d/);
	}, [formValues.password]);

	const passwordIncludesSpecialCharacters = useMemo(() => {
		return formValues.password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/);
	}, [formValues.password]);

	const passwordIncludesUppercase = useMemo(() => {
		return formValues.password.match(/[A-Z]/);
	}, [formValues.password]);

	const passwordIncludesLowercase = useMemo(() => {
		return formValues.password.match(/[a-z]/);
	}, [formValues.password]);

	const tileColor = useMemo(() => {
		if (passwordComplexity >= 5) {
			return "bg-status-success";
		} else if (passwordComplexity >= 3) {
			return "bg-status-warning";
		} else if (passwordComplexity >= 1) {
			return "bg-status-error";
		}

		return "bg-gray-200";
	}, [passwordComplexity]);

	return (
		<div className="w-full h-full flex items-center justify-center">
			<FormContainer
				description={ts("update-pass-description")}
				disabled={passwordComplexity < 5}
				formError={error}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("confirm")}
				title={ts("setPassword")}
			>
				<FormTextField
					autoComplete="password"
					endAdornment={
						<button
							type="button"
							onClick={() =>
								setPasswordFieldType(passwordFieldType === "password" ? "text" : "password")
							}
						>
							<Icon className="w-4 h-4 text-primary-gray-lighter" icon={faEye} />
						</button>
					}
					errorMessage={errors?.password?.message}
					formRegister={register("password")}
					label={ts("password")}
					startAdornment={<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faLock} />}
					type="password"
					onChange={(e) => {
						setValue("password", e.target.value);
					}}
				/>
				{formValues.password && formValues.password?.length > 0 && (
					<>
						<div className="flex flex-row gap-2">
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 1 ? tileColor : "bg-gray-200"
								)}
							></div>
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 3 ? tileColor : "bg-gray-200"
								)}
							></div>
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 5 ? tileColor : "bg-gray-200"
								)}
							></div>
						</div>

						<div className="text-left text-xs font-normal w-full mb-4">
							{formValues.password?.length < 8 && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.minimum")}
								</div>
							)}
							{!passwordIncludesNumbers && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.number")}{" "}
								</div>
							)}
							{!passwordIncludesSpecialCharacters && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.specialChar")}{" "}
								</div>
							)}
							{!passwordIncludesUppercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.uppercase")}{" "}
								</div>
							)}
							{!passwordIncludesLowercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.lowercase")}{" "}
								</div>
							)}
						</div>
					</>
				)}

				<FormTextField
					autoComplete="confirmPassword"
					endAdornment={
						<button
							type="button"
							onClick={() =>
								setPasswordFieldType(passwordFieldType === "password" ? "text" : "password")
							}
						>
							<Icon className="w-4 h-4 text-primary-gray-lighter" icon={faEye} />
						</button>
					}
					errorMessage={errors?.confirmPassword?.message}
					formRegister={register("confirmPassword")}
					label={ts("confirmPassword")}
					startAdornment={<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faLock} />}
					type="password"
				/>
			</FormContainer>
		</div>
	);
};
